import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import { SET_ERROR, SET_SUCCESS } from "../../store/Types/AuthTypes";
import { usePermissions } from "../../permissions/PermissionContext";

const ACTIONS = [
  { key: "view", label: "View" },
  { key: "create", label: "Create" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
];

// Create / edit a role: name + a module × action checkbox matrix.
export default function RoleForm() {
  const { roleId } = useParams();
  const isEdit = !!roleId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roleName: myRoleName, refresh } = usePermissions();

  const [modules, setModules] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState({});
  const [isSuperRole, setIsSuperRole] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [mods, role] = await Promise.all([
          backendVerifiedUser.get("/permission-modules"),
          isEdit ? backendVerifiedUser.get(`/roles/${roleId}`) : Promise.resolve(null),
        ]);
        setModules(mods.data || []);
        if (role?.data) {
          setName(role.data.name || "");
          setDescription(role.data.description || "");
          setPerms(role.data.permissions || {});
          setIsSuperRole(!!role.data.is_super);
        }
      } catch (err) {
        dispatch({ type: SET_ERROR, payLoad: err.response?.data?.error?.msg || "Could not load role" });
      } finally {
        setLoading(false);
      }
    })();
  }, [roleId]);

  const has = (m, a) => isSuperRole || !!perms[m]?.[a];

  // Toggling create/edit/delete on also turns view on; turning view off clears the row
  const toggle = (moduleKey, action, value) => {
    setPerms((prev) => {
      const row = { ...(prev[moduleKey] || {}) };
      row[action] = value;
      if (value && action !== "view") row.view = true;
      if (!value && action === "view") ["create", "edit", "delete"].forEach((a) => (row[a] = false));
      return { ...prev, [moduleKey]: row };
    });
  };

  const setRow = (mod, value) => {
    setPerms((prev) => ({ ...prev, [mod.key]: Object.fromEntries(mod.actions.map((a) => [a, value])) }));
  };

  const setColumn = (action, value) => {
    setPerms((prev) => {
      const next = { ...prev };
      modules.forEach((m) => {
        if (!m.actions.includes(action)) return;
        const row = { ...(next[m.key] || {}) };
        row[action] = value;
        if (value && action !== "view") row.view = true;
        if (!value && action === "view") ["create", "edit", "delete"].forEach((a) => (row[a] = false));
        next[m.key] = row;
      });
      return next;
    });
  };

  const columnState = (action) => {
    const relevant = modules.filter((m) => m.actions.includes(action));
    const on = relevant.filter((m) => has(m.key, action)).length;
    return { all: relevant.length > 0 && on === relevant.length, some: on > 0 };
  };

  const grantedCount = useMemo(
    () => modules.filter((m) => m.actions.some((a) => has(m.key, a))).length,
    [modules, perms, isSuperRole]
  );

  const save = async () => {
    setTouched(true);
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim(), permissions: perms };
      const res = isEdit
        ? await backendVerifiedUser.post(`/roles/${roleId}`, body)
        : await backendVerifiedUser.post("/roles", body);
      dispatch({ type: SET_SUCCESS, payLoad: res.data?.success?.msg });
      refresh(); // in case this is the current user's role
      navigate("/roles");
    } catch (err) {
      dispatch({ type: SET_ERROR, payLoad: err.response?.data?.error?.msg || "Could not save role" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <ProgressSpinner style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  const readOnly = isSuperRole;

  return (
    <div className="card shadow-md rounded-lg p-4 mx-3 mt-3">
      <Divider>
        <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
          {readOnly ? "Super Admin Role" : isEdit ? "Edit Role" : "Add Role"}
        </span>
      </Divider>

      <div className="flex justify-between flex-wrap gap-2 mb-4">
        <Button label="Back" icon="pi pi-arrow-left" className="p-primary-highlight-btn" onClick={() => navigate("/roles")} />
        {isEdit && myRoleName && myRoleName === name && !readOnly && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            This is your own role — only a Super Admin can change it.
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="block text-sm font-medium mb-1">Role name *</label>
          <InputText
            className={`w-full ${touched && !name.trim() ? "p-invalid" : ""}`}
            value={name}
            maxLength={100}
            disabled={readOnly}
            placeholder="e.g. Order Manager"
            onChange={(e) => setName(e.target.value)}
          />
          {touched && !name.trim() && <small className="p-error">Role name is required</small>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <InputText
            className="w-full"
            value={description}
            maxLength={255}
            disabled={readOnly}
            placeholder="What this role is for"
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      {readOnly && (
        <p className="text-sm text-gray-600 mb-3">
          Super Admin always has full access to every module. This role can't be edited or deleted.
        </p>
      )}

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold m-0">Permissions</h3>
        <span className="text-xs text-gray-500">
          {grantedCount} of {modules.length} modules enabled · Create / Edit / Delete automatically include View
        </span>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm" style={{ minWidth: 640 }}>
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="p-3 font-semibold">Module</th>
              {ACTIONS.map((a) => {
                const st = columnState(a.key);
                return (
                  <th key={a.key} className="p-3 font-semibold text-center" style={{ width: 110 }}>
                    <div className="flex flex-col items-center gap-1">
                      <span>{a.label}</span>
                      {!readOnly && (
                        <Checkbox
                          checked={st.all}
                          onChange={(e) => setColumn(a.key, e.checked)}
                          tooltip={`All modules: ${a.label}`}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
              {!readOnly && <th className="p-3 font-semibold text-center" style={{ width: 90 }}>All</th>}
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => {
              const rowAll = m.actions.every((a) => has(m.key, a));
              return (
                <tr key={m.key} className="border-t hover:bg-gray-50">
                  <td className="p-3">{m.label}</td>
                  {ACTIONS.map((a) => (
                    <td key={a.key} className="p-3 text-center">
                      {m.actions.includes(a.key) ? (
                        <Checkbox
                          inputId={`${m.key}-${a.key}`}
                          checked={has(m.key, a.key)}
                          disabled={readOnly}
                          onChange={(e) => toggle(m.key, a.key, e.checked)}
                          aria-label={`${m.label}: ${a.label}`}
                        />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="p-3 text-center">
                      <Checkbox checked={rowAll} onChange={(e) => setRow(m, e.checked)} aria-label={`${m.label}: all`} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-3 mt-5">
          <Button label="Cancel" className="p-red-btn" icon="pi pi-times" onClick={() => navigate("/roles")} />
          <Button
            label={saving ? "Saving…" : isEdit ? "Update Role" : "Create Role"}
            icon="pi pi-check"
            className="p-secondary-btn"
            disabled={saving}
            onClick={save}
          />
        </div>
      )}
    </div>
  );
}
